import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../Auth/AuthContext';
import { Line, Pie, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler } from 'chart.js';
import { apiCall } from '../../utils/apiUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler);

// Professional SVG Icons
const Icons = {
  orders: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  pending: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  clients: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  vendors: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
    </svg>
  ),
  assigned: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
};

// Professional color configurations - matching Invoice Dashboard design
const StatColors = {
  blue: {
    borderColor: 'border-blue-500',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
  },
  amber: {
    borderColor: 'border-amber-500',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  emerald: {
    borderColor: 'border-emerald-500',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
  },
  violet: {
    borderColor: 'border-violet-500',
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
  },
};

const DashboardStats = ({ stats, dateRange, setActiveTab, unreadNotificationCount }) => {
  const { user } = useAuth();

  // --- State Filter for Order Volume ---
  const [allStates, setAllStates] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [filteredVolumeData, setFilteredVolumeData] = useState(null);
  const stateDropdownRef = useRef(null);

  // Fetch all available states on mount
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await apiCall('/api/states/from-orders');
        setAllStates(response.states || []);
      } catch (error) {
        console.error('Error fetching states:', error);
      }
    };
    fetchStates();
  }, []);

  // Fetch filtered volume data when selectedStates or dateRange changes
  const fetchFilteredVolume = useCallback(async () => {
    if (selectedStates.length === 0) {
      setFilteredVolumeData(null); // Use global stats
      return;
    }
    try {
      const stateParams = selectedStates.map(s => `state=${encodeURIComponent(s)}`).join('&');
      const response = await apiCall(`/api/dashboard/stats/volume?range=${dateRange}&${stateParams}`);
      setFilteredVolumeData(response);
    } catch (error) {
      console.error('Error fetching filtered volume:', error);
      setFilteredVolumeData(null);
    }
  }, [selectedStates, dateRange]);

  useEffect(() => {
    fetchFilteredVolume();
  }, [fetchFilteredVolume]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(e.target)) {
        setStateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const MAX_STATES = 4;
  const toggleState = (stateName) => {
    setSelectedStates(prev => {
      if (prev.includes(stateName)) {
        return prev.filter(s => s !== stateName);
      }
      if (prev.length >= MAX_STATES) {
        return prev; // Limit reached, do not add more
      }
      return [...prev, stateName];
    });
  };

  const clearStateFilter = () => {
    setSelectedStates([]);
    setStateDropdownOpen(false);
  };

  // Use filtered data if available, otherwise fallback to global stats
  const activeVolumeStats = filteredVolumeData || stats;

  const getStatsCards = () => {
    if (user.role === 'admin') {
      return [
        {
          title: 'Total Orders',
          value: stats.total_orders || 0,
          colors: StatColors.blue,
          icon: Icons.orders,
          description: 'All time orders'
        },
        {
          title: 'Pending Orders',
          value: stats.pending_orders || 0,
          colors: StatColors.amber,
          icon: Icons.pending,
          description: 'Awaiting processing'
        },
        {
          title: 'Total Clients',
          value: stats.total_clients || 0,
          colors: StatColors.emerald,
          icon: Icons.clients,
          description: 'Active clients'
        },
        {
          title: 'Total Vendors',
          value: stats.total_vendors || 0,
          colors: StatColors.violet,
          icon: Icons.vendors,
          description: 'Active vendors'
        },
      ];
    } else if (user.role === 'client') {
      return [
        {
          title: 'My Orders',
          value: stats.total_orders || 0,
          colors: StatColors.blue,
          icon: Icons.orders,
          description: 'Your orders'
        },
        {
          title: 'Pending Orders',
          value: stats.pending_orders || 0,
          colors: StatColors.amber,
          icon: Icons.pending,
          description: 'In progress'
        },
      ];
    } else {
      return [
        {
          title: 'Assigned Orders',
          value: stats.assigned_orders || 0,
          colors: StatColors.blue,
          icon: Icons.assigned,
          description: 'Assigned to you'
        },
        {
          title: 'Pending Orders',
          value: stats.pending_orders || 0,
          colors: StatColors.amber,
          icon: Icons.pending,
          description: 'Awaiting completion'
        },
      ];
    }
  };

  // Order Volume Over Time (Line Chart)
  const orderVolumeData = useMemo(() => ({
    labels: activeVolumeStats.order_volume?.labels || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Order Volume',
        data: (activeVolumeStats.order_volume?.data || []).map(count => count / 100),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.08)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#3b82f6',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  }), [activeVolumeStats.order_volume]);

  // Order Completion Rates (Pie Chart)
  const totalOrders = (stats.completed_orders || 0) + (stats.pending_orders || 0) + (stats.rejected_orders || 0);
  const completionRateData = useMemo(() => ({
    labels: ['Completed', 'Pending', 'Rejected'],
    datasets: [
      {
        data: totalOrders ? [
          stats.completed_orders || 0,
          stats.pending_orders || 0,
          stats.rejected_orders || 0,
        ] : [0, 0, 0],
        backgroundColor: ['#10b981', '#f59e0b', '#ef4444'],
        borderColor: '#ffffff',
        borderWidth: 3,
        hoverOffset: 4,
      },
    ],
  }), [stats.completed_orders, stats.pending_orders, stats.rejected_orders, totalOrders]);

  // Orders per Client (Bar Chart)
  const ordersPerClientData = useMemo(() => ({
    labels: stats.orders_per_client?.labels || [],
    datasets: [
      {
        label: 'Orders by Client',
        data: (stats.orders_per_client?.data || []).map(count => count / 50),
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: '#10b981',
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
      },
    ],
  }), [stats.orders_per_client]);

  // Orders per Vendor (Bar Chart)
  const ordersPerVendorData = useMemo(() => ({
    labels: stats.orders_per_vendor?.labels || [],
    datasets: [
      {
        label: 'Orders by Vendor',
        data: (stats.orders_per_vendor?.data || []).map(count => count / 50),
        backgroundColor: 'rgba(139, 92, 246, 0.8)',
        borderColor: '#8b5cf6',
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
      },
    ],
  }), [stats.orders_per_vendor]);

  // Client Activity (Bar Chart)
  const activityData = useMemo(() => ({
    labels: user.role === 'admin' ? [] : [user.name || 'Your Activity'],
    datasets: [
      {
        label: 'Your Orders',
        data: user.role === 'admin' ? [] : [(stats.total_orders || 0) / 50],
        backgroundColor: 'rgba(139, 92, 246, 0.8)',
        borderColor: '#8b5cf6',
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
      },
    ],
  }), [stats.total_orders, user.role, user.name]);

  // Order Progress: Total Stamps (Red background) vs Completed (Blue overlay)
  // Implemented as Overlapping Bars (Grouped: false)
  // Dataset 0: Total Stamps (Red) - Drawn first (background)
  // Dataset 1: Completed Stamps (Blue) - Drawn second (foreground/overlap)
  const orderProgressData = useMemo(() => {
    const total = activeVolumeStats.order_progress?.total_orders || [];
    const completed = activeVolumeStats.order_progress?.completed_orders || [];

    return {
      labels: activeVolumeStats.order_progress?.labels || [],
      datasets: [
        {
          label: 'Total Stamps',
          data: total,
          backgroundColor: '#ef4444', // Red
          hoverBackgroundColor: '#dc2626',
          barPercentage: 0.6,
          categoryPercentage: 0.8,
          borderRadius: 4,
          grouped: false, // Allow overlap
          order: 1, // Background
        },
        {
          label: 'Completed Stamps',
          data: completed,
          backgroundColor: '#3b82f6', // Blue
          hoverBackgroundColor: '#2563eb',
          barPercentage: 0.6, // Same width to overlap perfectly
          categoryPercentage: 0.8,
          borderRadius: 4,
          grouped: false, // Allow overlap
          order: 0, // Foreground (drawn on top)
        },
      ],
    };
  }, [activeVolumeStats.order_progress]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          color: '#374151',
          font: { size: 12, weight: '500' },
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
        },
      },
      tooltip: {
        backgroundColor: '#1f2937',
        titleColor: '#fff',
        bodyColor: '#d1d5db',
        titleFont: { size: 13, weight: '600' },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y !== undefined ? context.parsed.y : (context.parsed || 0);
            if (label === 'Order Volume') {
              return `${value * 100} orders`;
            } else if (label === 'Orders by Client' || label === 'Orders by Vendor' || label === 'Your Orders') {
              return `${value * 50} orders`;
            } else {
              const percentage = totalOrders ? ((value / totalOrders) * 100).toFixed(1) : 0;
              return `${value} orders (${percentage}%)`;
            }
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#6b7280',
          font: { size: 11 },
          maxRotation: 45,
          minRotation: 45,
          callback: function (value) {
            const label = this.getLabelForValue(value);
            return label.length > 8 ? label.substring(0, 8) + '...' : label;
          },
        },
        grid: { display: false },
        border: { display: false },
      },
      y: {
        ticks: {
          color: '#6b7280',
          font: { size: 11 },
          callback: function (value) {
            if (this.chart.data.datasets[0].label === 'Order Volume') {
              return `${value * 100}`;
            }
            return `${value * 50}`;
          },
        },
        grid: {
          color: '#f3f4f6',
          drawBorder: false,
        },
        border: { display: false },
        beginAtZero: true,
      },
    },
  }), [totalOrders]);

  // Chart options specifically for the Order Progress graph
  const orderProgressChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: true,
    },
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          color: '#374151',
          font: { size: 11, weight: '500' },
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 16,
          boxWidth: 6,
        },
      },
      tooltip: {
        backgroundColor: '#1f2937',
        titleColor: '#fff',
        bodyColor: '#d1d5db',
        titleFont: { size: 13, weight: '600' },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y || 0;
            return ` ${label}: ${value}`;
          },
          footer: (tooltipItems) => {
            const dataIndex = tooltipItems[0].dataIndex;
            const total = activeVolumeStats.order_progress?.total_orders?.[dataIndex] || 0;
            const completed = activeVolumeStats.order_progress?.completed_orders?.[dataIndex] || 0;
            const pending = Math.max(0, total - completed);
            return ` Pending Stamps: ${pending}`;
          }
        },
      },
    },
    scales: {
      x: {
        stacked: false,
        ticks: {
          color: '#6b7280',
          font: { size: 10 },
          maxRotation: 45,
          minRotation: 45,
        },
        grid: { display: false },
        border: { display: false },
      },
      y: {
        stacked: false,
        title: {
          display: true,
          text: 'Stamps Count',
          color: '#9ca3af',
          font: { size: 11, weight: '500' },
        },
        ticks: {
          color: '#6b7280',
          font: { size: 10 },
          precision: 0,
        },
        grid: {
          color: '#f3f4f6',
          drawBorder: false,
        },
        border: { display: false },
        beginAtZero: true,
      },
    },
  }), [activeVolumeStats.order_progress]);

  const pieChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#374151',
          font: { size: 12, weight: '500' },
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 16,
        },
      },
      tooltip: {
        backgroundColor: '#1f2937',
        titleColor: '#fff',
        bodyColor: '#d1d5db',
        titleFont: { size: 13, weight: '600' },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const percentage = totalOrders ? ((value / totalOrders) * 100).toFixed(1) : 0;
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  }), [totalOrders]);

  // Chart Card Component
  const ChartCard = ({ title, children, subtitle }) => (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="px-6 py-5 border-b border-gray-50">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="p-6">
        <div className="h-64">
          {children}
        </div>
      </div>
    </div>
  );

  // Empty State Component
  const EmptyState = ({ message }) => (
    <div className="h-full flex flex-col items-center justify-center text-gray-400">
      <svg className="w-12 h-12 mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
      <p className="text-sm font-medium">{message}</p>
    </div>
  );

  return (
    <div className="space-y-2">
      {/* Stats Cards - Single Row Compact Design */}
      {(() => {
        const cards = getStatsCards();
        const colClass = cards.length <= 2
          ? 'grid-cols-1 md:grid-cols-2'
          : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';
        return (
          <div className="bg-gray-200 rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className={`grid ${colClass} gap-px bg-gray-200`}>
              {cards.map((stat, index) => (
                <div
                  key={index}
                  className={`bg-white p-4 border-l-4 ${stat.colors.borderColor} hover:bg-gray-50 transition-colors duration-150`}
                >
                  <div className="flex items-center">
                    <div className={`p-3 ${stat.colors.iconBg} rounded-xl mr-4`}>
                      <span className={`${stat.colors.iconColor} w-6 h-6 block`}>
                        {React.cloneElement(stat.icon, { className: "w-6 h-6" })}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{stat.title}</p>
                      <p className="text-xl font-bold text-gray-900 mt-0.5">{stat.value.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Order Stats - Floating Tabs Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {/* Left Column: 3 Cards */}
        <div className="flex flex-col gap-2">
          {/* Section 1: Order Volume */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-4 pt-4 pb-1 flex flex-col h-64">
            {/* State Filter Dropdown */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <h3 className="text-base font-semibold text-gray-900">Order Volume</h3>

              {/* Selected State Chips */}
              {selectedStates.map((stateName) => (
                <span
                  key={stateName}
                  className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full border border-blue-200"
                >
                  {stateName}
                  <button
                    onClick={() => toggleState(stateName)}
                    className="hover:text-blue-900 focus:outline-none ml-0.5"
                    title={`Remove ${stateName}`}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}

              {/* Spacer to push dropdown to right */}
              <div className="flex-1" />

              {/* Dropdown Button */}
              <div className="relative" ref={stateDropdownRef}>
                <button
                  type="button"
                  onClick={() => setStateDropdownOpen(!stateDropdownOpen)}
                  className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 hover:border-gray-300 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-600 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  <span>{selectedStates.length === 0 ? 'All States' : 'Filter'}</span>
                  <svg className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${stateDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {stateDropdownOpen && (
                  <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg border border-gray-200 shadow-lg z-50 overflow-hidden">
                    <div className="max-h-48 overflow-y-auto py-1">
                      <button
                        onClick={clearStateFilter}
                        className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center justify-between ${selectedStates.length === 0 ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        All States
                        {selectedStates.length === 0 && (
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                      {allStates.map((stateName) => {
                        const isSelected = selectedStates.includes(stateName);
                        const isDisabled = !isSelected && selectedStates.length >= MAX_STATES;
                        return (
                          <button
                            key={stateName}
                            onClick={() => !isDisabled && toggleState(stateName)}
                            className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center justify-between ${isSelected ? 'bg-blue-50 text-blue-700 font-medium' :
                              isDisabled ? 'text-gray-300 cursor-not-allowed' :
                                'text-gray-700 hover:bg-gray-50'
                              }`}
                            disabled={isDisabled}
                          >
                            {stateName}
                            {isSelected && (
                              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {selectedStates.length > 0 && (
                      <div className="border-t border-gray-100 px-3 py-2">
                        <button
                          onClick={clearStateFilter}
                          className="text-xs text-red-500 hover:text-red-700 font-medium"
                        >
                          Clear Filter
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 min-h-0">
              <div className="h-full">
                {orderVolumeData.labels.length > 0 && orderVolumeData.datasets[0].data.length > 0 ? (
                  <Line data={orderVolumeData} options={chartOptions} />
                ) : (
                  <EmptyState message="No volume data available" />
                )}
              </div>
            </div>
          </div>

          {/* Section 2: Procurement Status */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-4 pt-4 pb-1 flex flex-col h-64">
            <div className="mb-4">
              <h3 className="text-base font-semibold text-gray-900">Procurement Status</h3>
            </div>
            <div className="flex-1 min-h-0">
              <div className="h-full">
                {orderProgressData.labels.length > 0 && orderProgressData.datasets[0].data.length > 0 ? (
                  <Bar data={orderProgressData} options={orderProgressChartOptions} />
                ) : (
                  <EmptyState message="No progress data available" />
                )}
              </div>
            </div>
          </div>

          {/* Section 3: Orders per Client / Your Activity */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-4 pt-4 pb-1 flex flex-col h-64">
            {user.role === 'admin' ? (
              <>
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">Orders per Client</h3>
                </div>
                <div className="flex-1 min-h-0">
                  <div className="h-full">
                    {ordersPerClientData.labels.length > 0 && ordersPerClientData.datasets[0].data.length > 0 ? (
                      <Bar data={ordersPerClientData} options={chartOptions} />
                    ) : (
                      <EmptyState message="No client data available" />
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">Your Activity</h3>
                  <p className="text-sm text-gray-500 mt-0.5">Order summary</p>
                </div>
                <div className="flex-1 min-h-0">
                  <div className="h-full">
                    {activityData.datasets[0].data[0] > 0 ? (
                      <Bar data={activityData} options={chartOptions} />
                    ) : (
                      <EmptyState message="No activity data available" />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right Column: 2 Cards */}
        <div className="flex flex-col gap-2">
          {/* Order Status + Quick Actions + Recent Activity Refactored */}
          <div className="grid grid-cols-2 gap-4 h-96">
            {/* Left Half: Pie Chart Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col overflow-hidden h-full">
              <div className="mb-3">
                <h3 className="text-base font-semibold text-gray-900">Order Status</h3>
              </div>
              <div className="flex-1 min-h-0">
                <div className="h-full">
                  {totalOrders > 0 ? (
                    <Pie data={completionRateData} options={pieChartOptions} />
                  ) : (
                    <EmptyState message="No status data available" />
                  )}
                </div>
              </div>
            </div>

            {/* Right Half: Order Status only — Quick Actions moved to PlatformHome */}
            <div className="flex flex-col h-full">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col overflow-hidden h-full">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900">Order Completion</h3>
                </div>
                <div className="flex-1 min-h-0">
                  <div className="h-full">
                    {totalOrders > 0 ? (
                      <Pie data={completionRateData} options={pieChartOptions} />
                    ) : (
                      <EmptyState message="No status data available" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Orders per Vendor */}
          {user.role === 'admin' ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col h-96">
              <div className="mb-6">
                <h3 className="text-base font-semibold text-gray-900">Orders per Vendor</h3>
              </div>
              <div className="flex-1 min-h-0">
                <div className="h-full">
                  {ordersPerVendorData.labels.length > 0 && ordersPerVendorData.datasets[0].data.length > 0 ? (
                    <Bar data={ordersPerVendorData} options={chartOptions} />
                  ) : (
                    <EmptyState message="No vendor data available" />
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="hidden md:block flex-1">
              {/* Spacer for layout balance */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(DashboardStats);